// src/components/requests/RequestsHeader.jsx
import React from 'react';
import HeaderLogoTitle from '../shared/HeaderLogoTitle';
import SharedUserProfile from '../shared/SharedUserProfile';
import { useAuth } from '../../contexts/AuthContext';

const AppView = {
  HOME: 'HOME',
  RESULTS: 'RESULTS',
  PROVIDER: 'PROVIDER',
  USER_DASHBOARD: 'USER_DASHBOARD',
};

const RequestsHeader = ({ 
  currentView, 
  onChangeView, 
  isDarkMode, 
  onToggleTheme, 
  bookData,
  isOwner,
  navigate,
  setShowAuthModal,
  setShowProfileModal
}) => {
  const { user, userProfile, logout } = useAuth();
  const primaryColor = bookData?.styling?.primaryColor || '#6366f1';
  const providerColor = '#10b981';

  return (
    <header className={`sticky top-0 z-50 w-full backdrop-blur-md border-b transition-all duration-300 ${
      isDarkMode 
        ? 'bg-[#0f172a]/90 border-slate-800' 
        : 'bg-white/90 border-slate-100'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        
        {/* Logo & Title */}
        <div 
          className="flex-shrink-0 cursor-pointer" 
          onClick={() => onChangeView(AppView.HOME)}
        >
          <HeaderLogoTitle
            isOwner={isOwner}
            bookData={bookData}
            customStyling={{
              primaryColor: currentView === AppView.PROVIDER ? providerColor : primaryColor
            }}
            navigate={navigate}
          />
        </div>
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 flex-1 justify-center">
          <button 
            onClick={() => onChangeView(AppView.HOME)}
            className={`text-sm font-medium transition-colors ${
              currentView === AppView.HOME || currentView === AppView.RESULTS
                ? 'font-bold'
                : isDarkMode 
                  ? 'text-slate-400 hover:text-slate-200' 
                  : 'text-slate-600 hover:text-slate-900'
            }`}
            style={
              currentView === AppView.HOME || currentView === AppView.RESULTS
                ? { color: primaryColor }
                : {}
            }
          >
            Post a Request
          </button>
          
          <button 
            onClick={() => onChangeView(AppView.USER_DASHBOARD)}
            className={`text-sm font-medium transition-colors ${
              currentView === AppView.USER_DASHBOARD
                ? 'font-bold'
                : isDarkMode 
                  ? 'text-slate-400 hover:text-slate-200' 
                  : 'text-slate-600 hover:text-slate-900'
            }`}
            style={
              currentView === AppView.USER_DASHBOARD
                ? { color: primaryColor }
                : {}
            }
          >
            My Requests
          </button>
          
          <button 
            onClick={() => onChangeView(AppView.PROVIDER)}
            className={`text-sm font-medium transition-colors ${
              currentView === AppView.PROVIDER
                ? 'font-bold'
                : isDarkMode 
                  ? 'text-slate-400 hover:text-slate-200' 
                  : 'text-slate-600 hover:text-slate-900'
            }`}
            style={
              currentView === AppView.PROVIDER
                ? { color: providerColor }
                : {}
            }
          >
            For Providers
          </button>
          
          <div className={`h-4 w-px ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}></div>

          {/* Theme Toggle */}
          <button 
            onClick={onToggleTheme}
            className={`p-2 rounded-full transition-colors ${
              isDarkMode
                ? 'hover:bg-slate-800 text-slate-400'
                : 'hover:bg-slate-100 text-slate-500'
            }`}
            aria-label="Toggle Theme"
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </nav>

        {/* User Profile */}
        <div className="flex-shrink-0">
          <SharedUserProfile
            user={user}
            userProfile={userProfile}
            styling={bookData?.styling || {}}
            bookData={bookData}
            isOwner={isOwner}
            navigate={navigate}
            logout={logout}
            isDarkMode={isDarkMode}
            setShowAuthModal={setShowAuthModal}
            setShowProfileModal={setShowProfileModal}
            shouldShowUserProfile={true}
          />
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-2">
          <button 
            onClick={onToggleTheme}
            className={`p-2 rounded-full transition-colors ${
              isDarkMode
                ? 'hover:bg-slate-800 text-slate-400'
                : 'hover:bg-slate-100 text-slate-500'
            }`}
            aria-label="Toggle Theme"
          >
            {isDarkMode ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu (Below Header) */}
      <div className={`md:hidden border-t transition-colors ${
        isDarkMode ? 'border-slate-800 bg-[#0f172a]/95' : 'border-slate-200 bg-white/95'
      }`}>
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-around">
          <button 
            onClick={() => onChangeView(AppView.HOME)}
            className={`flex flex-col items-center gap-1 text-xs font-medium transition-colors ${
              currentView === AppView.HOME || currentView === AppView.RESULTS
                ? 'font-bold'
                : isDarkMode 
                  ? 'text-slate-400' 
                  : 'text-slate-600'
            }`}
            style={
              currentView === AppView.HOME || currentView === AppView.RESULTS
                ? { color: primaryColor }
                : {}
            }
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            <span>Request</span>
          </button>

          <button 
            onClick={() => onChangeView(AppView.USER_DASHBOARD)}
            className={`flex flex-col items-center gap-1 text-xs font-medium transition-colors ${
              currentView === AppView.USER_DASHBOARD
                ? 'font-bold'
                : isDarkMode 
                  ? 'text-slate-400' 
                  : 'text-slate-600'
            }`}
            style={
              currentView === AppView.USER_DASHBOARD
                ? { color: primaryColor }
                : {}
            }
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span>My Requests</span>
          </button>

          <button 
            onClick={() => onChangeView(AppView.PROVIDER)}
            className={`flex flex-col items-center gap-1 text-xs font-medium transition-colors ${
              currentView === AppView.PROVIDER
                ? 'font-bold'
                : isDarkMode 
                  ? 'text-slate-400' 
                  : 'text-slate-600'
            }`}
            style={
              currentView === AppView.PROVIDER
                ? { color: providerColor }
                : {}
            }
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>Providers</span>
          </button>
        </nav>
      </div>
    </header>
  );
};

export default RequestsHeader;